import React from "react"
import { useSelector, useDispatch } from "react-redux"

import Images from "assets/images"
import {
    VIEW, EDIT, DELETE,
    PROVEN_NO, PROVEN_IN_USE, PRIOR_USE,
} from "const"
import { SilProjectDevice } from "server/models"
import {
    PRM_SIL_DEVICE,
    PRM_SIL_SUBSYSTEM,
    StoreType,
    SilDeviceType,
    addModal,
} from "client/store"
import { hasAccess, hasProjectAccess } from "client/utils"
import { silSubsystemsRoute } from "client/parts/routes"
import {
    Logo,
    Link,
    DateFormatter,
    TableColumn,
    TablePopoverData,
    TablePopover,
    UpdateButton,
} from "client/components"

import SilUpdateDevice from "client/pages/sil/update/device"
import SilUpdateSubsystem from "client/pages/sil/update/subsystem"

const Columns = () => {
    const baseDeviceRoute = useSelector((state: StoreType) => state.silDevice.baseDeviceRoute)
    const baseSubsystemRoute = useSelector((state: StoreType) => state.silDevice.baseSubsystemRoute)

    const project = useSelector((state: StoreType) => state.project.projectView)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const isSubsystem = window.location.pathname.indexOf(silSubsystemsRoute) >= 0
    const permission = isSubsystem ? PRM_SIL_SUBSYSTEM : PRM_SIL_DEVICE

    const editAccess = !project.id
        ? hasAccess(permission, EDIT)
        : hasProjectAccess(permission, EDIT)

    const deleteAccess = !project.id
        ? hasAccess(permission, DELETE)
        : hasProjectAccess(permission, DELETE)

    const deviceLink = (device: SilDeviceType, linkContent: any) => {
        const href = `${isSubsystem ? baseSubsystemRoute : baseDeviceRoute}/${VIEW}/${device.id}`
        return (
            <Link
                href={href}
                onClick={() => dispatch(addModal({ url: href }))}
            >
                {linkContent}
            </Link>
        )
    }

    const columns: TableColumn[] = [
        {
            field: "id",
            header: lang.id,
            sortable: true,
        },
        {
            field: "logo",
            header: lang.logo,
            centerAlign: true,
            formatter: (device: SilDeviceType) => {
                const projectDevice = device as SilProjectDevice
                const isQidDevice = project.id && projectDevice.qidProductId !== null
                return deviceLink(device, <Logo table logo={device.logo || (isQidDevice ? Images.qidProductLogo : Images.silDeviceLogo)} />)
            },
        },
        {
            field: "name",
            header: lang.name,
            sortable: true,
            cellMinWidth: "100px",
            cellMaxWidth: "300px",
            formatter: (device: SilDeviceType) => deviceLink(device, device.name),
        },
    ]

    if (project.id !== null) {
        columns.push({
            field: "qidModelName",
            header: lang.model,
            sortable: false,
            cellMinWidth: "150px",
            cellMaxWidth: "300px",
            formatter: (device: SilDeviceType) => {
                const projectDevice = device as SilProjectDevice
                return projectDevice.qidModelName
            },
        })
    }

    columns.push(
        {
            field: "manufacturer",
            header: lang.manufacturer,
            sortable: true,
            cellMinWidth: "150px",
            cellMaxWidth: "300px",
        },
        {
            field: "type",
            header: lang.type,
            sortable: true,
            cellMinWidth: "100px",
        },
        {
            field: "mode",
            header: lang.mode,
            sortable: false,
            cellMinWidth: "150px",
            formatter: (device: SilDeviceType) => {
                const dataList: TablePopoverData[] = []

                if (device.lowDemandMode) {
                    dataList.push({
                        id: `${device.id}lowDemandMode`,
                        title: lang.modeLowDemand,
                    })
                }
                if (device.highDemandMode) {
                    dataList.push({
                        id: `${device.id}highDemandMode`,
                        title: lang.modeHighDemand,
                    })
                }
                if (device.continuousMode) {
                    dataList.push({
                        id: `${device.id}continuousMode`,
                        title: lang.modeContinuous,
                    })
                }

                return (
                    <TablePopover
                        id={`${device.id}modeList`}
                        dataList={dataList}
                        placement="right"
                        title={lang.modes}
                        editAccess={false}
                    />
                )
            },
        },
        {
            field: "logicSolver",
            header: lang.logicSolver,
            sortable: true,
            cellMinWidth: "150px",
            formatter: (device: SilDeviceType) => (device.logicSolver ? lang.yes : lang.no),
        },
        {
            field: "provenInUse",
            header: lang.provenReliabilityData,
            sortable: true,
            cellMinWidth: "200px",
            formatter: (device: SilDeviceType) => {
                if (device.provenInUse === PROVEN_NO) return lang.no
                if (device.provenInUse === PROVEN_IN_USE) return lang.provenInUse
                if (device.provenInUse === PRIOR_USE) return lang.priorUse
                return null
            },
        },
        {
            field: "status",
            header: lang.status,
            centerAlign: true,
            sortable: true,
            editAccess: () => editAccess,
        },
    )

    if (isSubsystem || project.id !== null) {
        columns.push({
            field: "updated",
            header: lang.updated,
            centerAlign: true,
            sortable: true,
            cellMinWidth: "100px",
            formatter: (device: SilDeviceType) => device.updated && (
                <UpdateButton onClick={() => {
                    dispatch(addModal({
                        key: "updateDevice",
                        content: device.nested
                            ? <SilUpdateSubsystem deviceId={device.id} />
                            : <SilUpdateDevice deviceId={device.id} />,
                    }))
                }}
                />
            ),
        })
    }

    columns.push({
        field: "createdAt",
        header: lang.created,
        sortable: true,
        formatter: (device: SilDeviceType) => <DateFormatter date={device.createdAt} />,
    })

    if (deleteAccess) {
        columns.push({
            field: "delete",
            centerAlign: true,
            deleteAccess: () => true,
        })
    }

    return columns
}
export default Columns
